import { SettingTwoTone } from '@ant-design/icons'
import { Button, DatePicker, Flex, SelectProps, Tag } from 'antd'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { ChangeEvent, Dispatch, SetStateAction, useMemo, useState } from 'react'

import { InventoryDocumentState } from 'src/api/requests'
import { InternalInstitutionsSelect, SearchBar, Text } from 'src/components'
import { SelectedItemsState } from 'src/features/stock/types'
import { dayMonthYearBackSlash } from 'src/libs'

import { StatesSelect } from '../StatesSelect'
import { useStyles } from '../stock/styles'

const { RangePicker } = DatePicker

type Props = {
  selectedItems: SelectedItemsState
  setSelectedItems: Dispatch<SetStateAction<SelectedItemsState>>
}

enum FilterKeys {
  legalEntityIds = 'legalEntityIds',
  inDate = 'inDate',
  states = 'states'
}

export const IssuanceFilters = ({ selectedItems, setSelectedItems }: Props) => {
  const [showFilters, setShowFilters] = useState(true)

  const { styles } = useStyles()

  const palette = useTheme()

  const onSelectFilter = (
    key: string,
    value: string | string[],
    name: string,
    title: string
  ) => {
    setSelectedItems((prev) => {
      const currentValues = prev[key]?.values || []
      const currentNames = prev[key]?.names || []

      const newValues = Array.isArray(value) ? value : [value]

      return {
        ...prev,
        [key]: {
          title: t(`stock_filters.${title}`) || title,
          values: [...new Set([...currentValues, ...newValues])],
          names: [...new Set([...currentNames, name])]
        }
      }
    })
  }

  const onDateFilter = (key: string, value: string[], title: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [key]: {
        title: `${title}: ${value[0]} - ${value[1]}`,
        values: value,
        names: value
      }
    }))
  }

  const onDateTagRemove = (key: string) => {
    setSelectedItems((prev) => {
      const updatedItems = { ...prev }
      delete updatedItems[key]

      return updatedItems
    })
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      onSearchClear()
    }
  }

  const onSearch = (value: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      search: {
        title: t('common.invoice_number'),
        values: [value],
        names: [value]
      }
    }))
  }

  const onSearchClear = () => {
    setSelectedItems((prev) => ({
      ...prev,
      search: {
        title: '',
        values: [],
        names: []
      }
    }))
  }

  const onRemoveTagFilter = (key: string, input: string) => {
    const isDateKey = ['inDate'].includes(key)

    setSelectedItems((prev) => {
      if (isDateKey) {
        const updatedItems = { ...prev }
        delete updatedItems[key]
        return updatedItems
      }

      const currentValues = prev[key]?.values || []
      const currentNames = prev[key]?.names || []

      const nameIndex = currentNames.indexOf(input)
      const valueIndex = currentValues.indexOf(input)

      const indexToRemove = nameIndex !== -1 ? nameIndex : valueIndex

      if (indexToRemove === -1) return prev

      const updatedNames = currentNames.filter(
        (_, index) => index !== indexToRemove
      )
      const updatedValues = currentValues.filter(
        (_, index) => index !== indexToRemove
      )

      return {
        ...prev,
        [key]: {
          title: prev[key]?.title || '',
          values: updatedValues,
          names: updatedNames
        }
      }
    })
  }

  const tags = useMemo(
    () =>
      Object.entries(selectedItems).flatMap(
        ([key, { title, names, values }]) => {
          if (!values?.length || values.every((value) => value === '')) {
            return []
          }

          if (key === 'inDate') {
            return [
              {
                key: `${key}-${values.join('-')}`,
                label: `${title}`,
                keyCategory: key
              }
            ]
          }

          return names.map((name, index) => ({
            key: `${key}-${values[index]}`,
            label: `${title}: ${name}`,
            keyCategory: key
          }))
        }
      ),
    [selectedItems]
  )

  const renderSelect = (
    Component: React.ComponentType<SelectProps>,
    key: string
  ) => (
    <Component
      key={key}
      value={selectedItems[key]?.values || []}
      maxTagCount={1}
      className={styles.select}
      tagRender={({ label }) => (
        <Flex align="center">
          <Tag className={styles.selectTag}>{label}</Tag>
        </Flex>
      )}
      onSelect={(value, option) =>
        onSelectFilter(key, value, option?.label as string, key)
      }
      onClear={() => onClearAllValues(key)}
      onDeselect={(value) => onRemoveTagFilter(key, value)}
    />
  )

  const onClearAllValues = (key: string) => {
    setSelectedItems((prevItems) => ({
      ...prevItems,
      [key]: { names: [], values: [], title: '' }
    }))
  }

  const filterComponentsMap = {
    states: (key: string) => (
      <StatesSelect
        onSelect={(value, option) =>
          onSelectFilter(key, value, option?.label as string, key)
        }
        excludeStates={[InventoryDocumentState.MODIFIED_AND_ACCEPTED]}
        allowClear
        onClear={() => onClearAllValues(key)}
        onDeselect={(value) => onRemoveTagFilter(key, value)}
        className="full-width"
      />
    ),
    legalEntityIds: (key: string) => (
      <InternalInstitutionsSelect
        mode="multiple"
        value={selectedItems[key]?.values || []}
        maxTagCount={1}
        tagRender={({ label }) => (
          <Flex align="center">
            <Tag className={styles.selectTag}>{label}</Tag>
          </Flex>
        )}
        onClear={() => onClearAllValues(key)}
        onSelect={(value, option) =>
          onSelectFilter(key, value, option?.label as string, key)
        }
        onDeselect={(value) => onRemoveTagFilter(key, value)}
      />
    ),
    inDate: (key: string) => (
      <RangePicker
        allowClear
        className="full-width"
        format={dayMonthYearBackSlash}
        placeholder={[
          t('stock_issuance.issuance_from'),
          t('stock_issuance.issuance_from')
        ]}
        value={
          selectedItems[key]?.values?.length === 2
            ? [
                dayjs(selectedItems[key].values[0], dayMonthYearBackSlash),
                dayjs(selectedItems[key].values[1], dayMonthYearBackSlash)
              ]
            : null
        }
        onChange={(_, dateStrings) =>
          dateStrings?.[0] === ''
            ? onDateTagRemove(key)
            : onDateFilter(key, dateStrings, t('stock_issuance.issuance_from'))
        }
      />
    )
  }

  const filterComponents = (key: string) => {
    const Component =
      filterComponentsMap[key as keyof typeof filterComponentsMap]

    if (!Component) return null

    if (key === 'inDate' || key === 'legalEntityIds' || key === 'states') {
      return Component(key)
    }

    return renderSelect(Component as React.ComponentType<SelectProps>, key)
  }

  return (
    <Flex vertical gap={16} className="full-width">
      <Flex gap={16}>
        <SearchBar
          enterButton
          placeholder={t('common.search')}
          className="full-width"
          onSearch={onSearch}
          onChange={onChange}
          onClear={onSearchClear}
        />
        <Button
          color="primary"
          icon={<SettingTwoTone />}
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <Text color={palette.colorPrimary}>{t('common.filters')}</Text>
        </Button>
      </Flex>

      {showFilters && (
        <Flex gap={16} wrap>
          {Object.keys(FilterKeys)?.map((column) => (
            <div key={column} style={{ minWidth: '308px' }}>
              {filterComponents(column)}
            </div>
          ))}
        </Flex>
      )}

      <Flex gap={8} wrap>
        {tags?.map(({ key, label, keyCategory }) => (
          <Tag
            key={key}
            closable
            onClose={() => onRemoveTagFilter(keyCategory, label.split(': ')[1])}
          >
            {label}
          </Tag>
        ))}
      </Flex>
    </Flex>
  )
}
